<script lang="ts">
  import { features } from '../../features/feature-state.svelte';
  import { DEBUG_TOOLS } from '../../features/overrides';
  import { DebugCommandType } from '../../protocol/gen/warinspace/v2/common_pb.js';

  const GRANT = 1000;

  const TIMESCALES = [25, 50, 100, 200, 400, 800];

  const { oncommand }: { oncommand?: (type: DebugCommandType, amount: number) => void } = $props();

  let open = $state(false);
  let timescale = $state(100);

  function choice(key: string): 'auto' | 'on' | 'off' {
    const forced = features.overrides[key];
    if (forced === undefined) return 'auto';
    return forced ? 'on' : 'off';
  }

  function choose(key: string, pick: 'auto' | 'on' | 'off'): void {
    features.override(key, pick === 'auto' ? null : pick === 'on');
  }
</script>

<div class="debug" data-testid="debug-menu">
  {#if open}
    <div class="debug__panel">
      <div class="debug__head">
        <span class="debug__ring">{features.ring}</span>
        <button
          class="debug__close"
          type="button"
          aria-label="Close debug menu"
          onclick={() => (open = false)}>×</button
        >
      </div>

      {#each features.flags as flag (flag.key)}
        <div class="debug__flag">
          <span class="debug__key" class:debug__key--on={flag.enabled}>{flag.key}</span>
          <span class="debug__picker">
            {#each ['auto', 'on', 'off'] as const as pick (pick)}
              <button
                class="debug__pick"
                class:debug__pick--active={choice(flag.key) === pick}
                type="button"
                data-testid={`debug-${flag.key}-${pick}`}
                onclick={() => {
                  choose(flag.key, pick);
                }}>{pick}</button
              >
            {/each}
          </span>
        </div>
      {/each}

      {#if features.enabled(DEBUG_TOOLS) && oncommand !== undefined}
        <div class="debug__tools">
          <button
            class="debug__tool"
            type="button"
            data-testid="debug-give-credits"
            onclick={() => {
              oncommand(DebugCommandType.GRANT_CREDITS, GRANT);
            }}>{GRANT} credits</button
          >
          <button
            class="debug__tool"
            type="button"
            data-testid="debug-give-scrap"
            onclick={() => {
              oncommand(DebugCommandType.GRANT_SCRAP_SELF, GRANT);
            }}>{GRANT} scrap</button
          >
          <button
            class="debug__tool"
            type="button"
            data-testid="debug-give-scrap-all"
            onclick={() => {
              oncommand(DebugCommandType.GRANT_SCRAP_EVERYONE, GRANT);
            }}>{GRANT} scrap to all</button
          >
          <label class="debug__timescale">
            <span>timescale</span>
            <select
              data-testid="debug-timescale"
              bind:value={timescale}
              onchange={() => {
                oncommand(DebugCommandType.SET_TIMESCALE, timescale);
              }}
            >
              {#each TIMESCALES as percent (percent)}
                <option value={percent}>{percent / 100}x</option>
              {/each}
            </select>
          </label>
        </div>
      {/if}

      <button
        class="debug__clear"
        type="button"
        data-testid="debug-clear"
        onclick={() => {
          features.clearOverrides();
        }}>Clear overrides</button
      >
    </div>
  {:else}
    <button class="debug__pill" type="button" data-testid="debug-open" onclick={() => (open = true)}
      >debug</button
    >
  {/if}
</div>

<style>
  .debug {
    position: fixed;
    right: 8px;
    bottom: 8px;
    z-index: 900;
    font:
      11px/1.5 ui-monospace,
      monospace;
    color: #cfe4ff;
  }

  .debug__pill,
  .debug__panel {
    background: rgb(8 14 24 / 92%);
    border: 1px solid #2c4a6e;
    border-radius: 4px;
  }

  .debug__pill {
    padding: 3px 8px;
    color: inherit;
    font: inherit;
    cursor: pointer;
    opacity: 0.55;
  }

  .debug__pill:hover {
    opacity: 1;
  }

  .debug__panel {
    min-width: 240px;
    padding: 6px 8px 8px;
  }

  .debug__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .debug__ring {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #ffb84d;
  }

  .debug__close {
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    font-size: 14px;
    cursor: pointer;
  }

  .debug__flag {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 2px 0;
  }

  .debug__key {
    opacity: 0.5;
  }

  .debug__key--on {
    opacity: 1;
  }

  .debug__picker {
    display: flex;
  }

  .debug__pick {
    padding: 1px 6px;
    background: none;
    border: 1px solid #2c4a6e;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .debug__pick + .debug__pick {
    border-left: none;
  }

  .debug__pick--active {
    background: #1d3557;
    color: #fff;
  }

  .debug__tools {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #2c4a6e;
  }

  .debug__tool,
  .debug__timescale select {
    padding: 2px 6px;
    background: none;
    border: 1px solid #2c4a6e;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }

  .debug__timescale {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .debug__timescale select {
    background: #0a1220;
  }

  .debug__clear {
    width: 100%;
    margin-top: 8px;
    padding: 3px;
    background: none;
    border: 1px solid #2c4a6e;
    color: inherit;
    font: inherit;
    cursor: pointer;
  }
</style>
